import { Scalars } from '@varo/graphql-schema-types';
import varoUserIconRaw from 'assets/images/vxl/component/userAvatar/varo-user-icon.png';
import React from 'react';
import {
  Image,
  ImageSourcePropType,
  StyleSheet,
  // eslint-disable-next-line no-restricted-imports
  Text,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import ExpressiveMdText from 'shared/components/vxl/typography/expressive/ExpressiveMdText';
import ExpressiveXXSText from 'shared/components/vxl/typography/expressive/ExpressiveXXSText';
import ExpressiveXXXLText from 'shared/components/vxl/typography/expressive/ExpressiveXXXLText';
import theme from 'shared/styles/theme';

import {
  defaultColors,
  genericIconSize,
  getInitials,
  Size,
  UserAvatarTheme,
} from './UserAvatar.helpers';

// should be imported from GQL. Not in graphql-schema-types yet
export type UserAvatarInitials = {
  initials: Scalars['Copy'];
};

// should be imported from GQL. Not in graphql-schema-types yet
export type UserAvatarPhoto = {
  url: Scalars['URL'];
};

interface UserAvatarMainProps {
  userAvatarSize: Size;
  userAvatarShowVaroLogo: boolean;
}

type UserAvatarGenericPhotoProps = UserAvatarMainProps;

interface UserAvatarInitialProps extends UserAvatarMainProps {
  userAvatarTheme: UserAvatarTheme;
  userAvatarVariant: UserAvatarInitials;
}

interface UserAvatarPhotoProps extends UserAvatarMainProps {
  userAvatarTheme: never;
  userAvatarVariant: UserAvatarPhoto;
}

interface UserAvatarOptionalProps {
  userAvatarTheme?: UserAvatarTheme | undefined;
  userAvatarVariant?: UserAvatarInitials | UserAvatarPhoto;
}

export type UserAvatarProps =
  | UserAvatarGenericPhotoProps
  | UserAvatarPhotoProps
  | UserAvatarInitialProps;

const VaroLogo = () => {
  return (
    <Image
      style={styles.varoLogo}
      source={varoUserIconRaw as ImageSourcePropType}
      accessibilityLabel="varo-logo"
      accessibilityRole="image"
      role="img"
    />
  );
};

interface InitialsProps {
  size: Size;
  initials: string;
  style?: TextStyle | ViewStyle | (TextStyle & ViewStyle)[];
}

const Initials = ({ size, initials, style }: InitialsProps) => {
  switch (size) {
    case Size.Small:
      return (
        <Text style={[style as TextStyle, fontStyles.small]}>{initials}</Text>
      );
    case Size.Medium:
      return (
        <ExpressiveXXSText style={style as TextStyle}>
          {initials}
        </ExpressiveXXSText>
      );
    case Size.Large:
      return (
        <ExpressiveMdText style={style as TextStyle}>
          {initials}
        </ExpressiveMdText>
      );
    case Size.Xlarge:
      return (
        <ExpressiveXXXLText style={style as TextStyle}>
          {initials}
        </ExpressiveXXXLText>
      );
  }
};

const UserAvatar = ({
  /** Mandatory, Used to define the size of the UserAvatar. 4 Options - 'small', 'medium', 'large', 'xlarge' */
  userAvatarSize,
  /** Mandatory, Used to define if the Varo logo should be displayed on the UserAvatar. (only works on size Medium Avatars) */
  userAvatarShowVaroLogo,
  /** Optional, Used to define the background color and font color of the UserAvatar when user initials are displayed.
   *  - 5 Options
   *    - Lavender: ['lavender', 'wine']
   *    - Forest: ['forest', 'orchid']
   *    - Sunset: ['sunset', 'sand']
   *    - Lime: ['lime', 'forest']
   *    - Eggplant: ['eggplant', 'blush']
   */
  // userAvatarTheme,
  /** Optional, Used to define if a UserAvatar is a user provided image or user's initials. */
  // userAvatarVariant,
  ...props
}: UserAvatarProps & UserAvatarOptionalProps) => {
  const sizeStyle = styles[userAvatarSize];
  const { userAvatarTheme, userAvatarVariant } =
    props as UserAvatarOptionalProps;

  const isAvatarPhoto = (
    variant: UserAvatarInitials | UserAvatarPhoto,
  ): variant is UserAvatarPhoto => {
    return (variant as UserAvatarPhoto)?.url !== undefined;
  };

  const isAvatarInitials = (
    variant: UserAvatarInitials | UserAvatarPhoto,
  ): variant is UserAvatarInitials => {
    return (
      (variant as UserAvatarInitials)?.initials !== undefined &&
      (variant as UserAvatarInitials).initials.length > 0
    );
  };

  const avatarPhoto = userAvatarVariant && isAvatarPhoto(userAvatarVariant);
  const avatarInitials =
    userAvatarVariant && isAvatarInitials(userAvatarVariant);

  const accessibilityLabel = avatarPhoto
    ? 'user-provided-avatar'
    : 'generic-avatar';

  const showLogo = userAvatarShowVaroLogo && userAvatarSize === Size.Medium;
  const iconSizing = genericIconSize(userAvatarSize);

  if (avatarInitials && userAvatarTheme) {
    const initials = userAvatarVariant.initials as string;
    const userInitials = getInitials(initials);

    return (
      <View style={[styles.position, sizeStyle]}>
        <Initials
          style={[
            fontStyles.text,
            sizeStyle,
            defaultColors({
              font: userAvatarTheme[1],
              background: userAvatarTheme[0],
            }),
          ]}
          size={userAvatarSize}
          initials={userInitials}
        />
        {showLogo ? <VaroLogo /> : null}
      </View>
    );
  }

  return (
    <View style={[styles.position, sizeStyle]}>
      {avatarPhoto ? (
        <Image
          style={sizeStyle}
          source={
            {
              uri: userAvatarVariant.url as string,
            } as ImageSourcePropType
          }
          accessibilityLabel={accessibilityLabel}
          accessibilityRole="image"
          role="img"
        />
      ) : (
        <View
          style={[
            styles.genericIconBackground,
            { padding: iconSizing.padding },
          ]}
        >
          <Icon
            iconKey="GENERIC_CONTACT"
            iconColor={theme.color.interactive.primary}
            iconAccessibilityLabel="GENERIC_CONTACT"
            iconSize={iconSizing.iconSize}
          />
        </View>
      )}
      {showLogo ? <VaroLogo /> : null}
    </View>
  );
};

const fontStyles = StyleSheet.create({
  small: {
    ...theme.typography.expressive.xxs,
    fontSize: 15,
    lineHeight: 10,
  },
  text: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    textAlign: 'center',
  },
});

const styles = StyleSheet.create({
  /* eslint-disable react-native/no-unused-styles -- dynamic style keys require disabling the rule */
  [Size.Large]: {
    borderRadius: 68 / 2,
    height: 68,
    width: 68,
  },

  [Size.Medium]: {
    borderRadius: 40 / 2,
    height: 40,
    // TODO: JT-118301 This looks better than 26, but this component should be cleaned up to use <Text> instead of typography components
    lineHeight: 28,
    width: 40,
  },

  [Size.Small]: {
    borderRadius: 24 / 2,
    height: 24,
    width: 24,
  },

  [Size.Xlarge]: {
    borderRadius: 160 / 2,
    height: 160,
    width: 160,
  },
  /* eslint-enable react-native/no-unused-styles */
  genericIconBackground: {
    backgroundColor: theme.color.expressive.orchid,
    borderRadius: '50%',
  },
  position: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  varoLogo: {
    height: 17,
    left: 28,
    position: 'absolute',
    top: 28,
    width: 17,
  },
});

export default UserAvatar;
